import axios from 'axios';

import { broncoURL } from 'Vulture/ENV.js';
import { getCurrentAccountUUID } from 'Vulture/Session';

// installAccountHeaderInterceptor wires the currently-selected account onto every
// bronco-bound request as the `X-Account-UUID` header, from ONE place. The backend
// uses it to lock a non-global user's role/scope to that account. Centralizing it
// (rather than editing each of the ~30 request builders) guarantees no call site can
// silently omit the header and fall back to cross-account access.
//
// Covers all transports used to reach bronco: raw fetch, kali (fetch-based), and
// axios (Data.js).
let installed = false;

// attach returns a PLAIN object containing all of `headers` plus X-Account-UUID.
// It must normalize a Headers instance (kali builds one) or an entries array — a naive
// object spread `{...new Headers(...)}` yields {} and SILENTLY DROPS every header
// (including X-Auth-Session), which 401s the request. Returning a plain object works for
// both axios (config.headers) and fetch (init.headers).
function attach(headers, accountUUID) {
	const merged = {};
	if (headers instanceof Headers) {
		headers.forEach((value, key) => { merged[key] = value; });
	} else if (Array.isArray(headers)) {
		headers.forEach(([ key, value, ]) => { merged[key] = value; });
	} else if (headers) {
		Object.assign(merged, headers);
	}
	merged['X-Account-UUID'] = accountUUID;
	return merged;
}

export function installAccountHeaderInterceptor() {
	if (installed) {
		return;
	}
	installed = true;

	// axios (Data.js and any other axios usage)
	axios.interceptors.request.use((config) => {
		const url = config.url || '';
		const accountUUID = getCurrentAccountUUID();
		if (accountUUID && typeof url === 'string' && url.indexOf(broncoURL) === 0) {
			config.headers = attach(config.headers, accountUUID);
		}
		return config;
	});

	// fetch (most modules + kali, which is fetch-based)
	const originalFetch = window.fetch.bind(window);
	window.fetch = function (input, init) {
		const url = typeof input === 'string' ? input : (input && input.url) || '';
		const accountUUID = getCurrentAccountUUID();
		if (accountUUID && typeof url === 'string' && url.indexOf(broncoURL) === 0) {
			init = { ...(init || {}) };
			// Headers may live on a Request input rather than init; merge them so setting
			// init.headers doesn't override (drop) them — e.g. X-Auth-Session.
			let base = init.headers;
			if (base == null && input && typeof input !== 'string' && input.headers) {
				base = input.headers;
			}
			init.headers = attach(base, accountUUID);
		}
		return originalFetch(input, init);
	};
}
